import { budgetsApi } from '../../services/budgetsApi';

const state = {
  budgets: [],
  currentBudget: null,
  budgetTemplates: [],
  spendingCategories: [],
  spendingEntries: [],
  loading: false,
  error: null,
};

/* eslint-disable no-param-reassign */
const mutations = {
  SET_LOADING(currentState, loading) {
    currentState.loading = loading;
  },

  SET_ERROR(currentState, error) {
    currentState.error = error;
  },

  CLEAR_ERROR(currentState) {
    currentState.error = null;
  },

  SET_BUDGETS(currentState, budgets) {
    currentState.budgets = budgets;
  },

  SET_CURRENT_BUDGET(currentState, budget) {
    currentState.currentBudget = budget;
  },

  SET_BUDGET_TEMPLATES(currentState, templates) {
    currentState.budgetTemplates = templates;
  },

  SET_SPENDING_CATEGORIES(currentState, categories) {
    currentState.spendingCategories = categories;
  },

  SET_SPENDING_ENTRIES(currentState, entries) {
    currentState.spendingEntries = entries;
  },

  ADD_BUDGET(currentState, budget) {
    currentState.budgets.unshift(budget);
  },

  UPDATE_BUDGET(currentState, updatedBudget) {
    const index = currentState.budgets.findIndex((budget) => budget.id === updatedBudget.id);
    if (index !== -1) {
      currentState.budgets.splice(index, 1, updatedBudget);
    }
    if (currentState.currentBudget && currentState.currentBudget.id === updatedBudget.id) {
      currentState.currentBudget = updatedBudget;
    }
  },

  REMOVE_BUDGET(currentState, budgetId) {
    currentState.budgets = currentState.budgets.filter((budget) => budget.id !== budgetId);
    if (currentState.currentBudget && currentState.currentBudget.id === budgetId) {
      currentState.currentBudget = null;
    }
  },

  ADD_SPENDING_ENTRY(currentState, entry) {
    currentState.spendingEntries.unshift(entry);
  },

  UPDATE_SPENDING_ENTRY(currentState, updatedEntry) {
    const index = currentState.spendingEntries.findIndex((entry) => entry.id === updatedEntry.id);
    if (index !== -1) {
      currentState.spendingEntries.splice(index, 1, updatedEntry);
    }
  },

  REMOVE_SPENDING_ENTRY(currentState, entryId) {
    currentState.spendingEntries = currentState.spendingEntries.filter(
      (entry) => entry.id !== entryId,
    );
  },
};
/* eslint-enable no-param-reassign */

const actions = {
  async fetchBudgets({ commit }, filters = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await budgetsApi.getUserBudgets(filters);
      commit('SET_BUDGETS', response.data.budgets || []);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch budgets';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchBudgetDetails({ commit }, budgetId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await budgetsApi.getBudgetDetails(budgetId);
      commit('SET_CURRENT_BUDGET', response.data.budget);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch budget details';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchBudgetTemplates({ commit }, incomeRange) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await budgetsApi.getBudgetTemplates(incomeRange);
      commit('SET_BUDGET_TEMPLATES', response.data.templates || []);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch budget templates';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async generateAIBudget({ commit }, budgetData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await budgetsApi.generateAIBudget(budgetData);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to generate AI budget';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createBudget({ commit }, budgetData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await budgetsApi.createUserBudget(budgetData);
      commit('ADD_BUDGET', response.data.budget);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create budget';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateBudget({ commit }, { budgetId, budgetData }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await budgetsApi.updateBudget(budgetId, budgetData);
      commit('UPDATE_BUDGET', response.data.budget);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update budget';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteBudget({ commit }, budgetId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      await budgetsApi.deleteBudget(budgetId);
      commit('REMOVE_BUDGET', budgetId);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete budget';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async trackSpending({ commit }, { budgetId, spendingData }) {
    // Don't set global loading state to avoid clearing budget cards
    commit('CLEAR_ERROR');

    try {
      const response = await budgetsApi.trackSpending(budgetId, spendingData);
      commit('ADD_SPENDING_ENTRY', response.data.entry);
      // Update budget with new spending totals if provided
      if (response.data.budget) {
        commit('UPDATE_BUDGET', response.data.budget);
      }
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to track spending';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  // eslint-disable-next-line no-unused-vars
  async fetchSpendingEntries({ commit }, { budgetId, filters = {} }) {
    // Don't set global loading state to avoid clearing budget cards
    commit('CLEAR_ERROR');

    try {
      // Disabled API call - simulate successful response
      const mockResponse = {
        data: {
          entries: [], // Return empty array for now
        },
      };

      commit('SET_SPENDING_ENTRIES', mockResponse.data.entries || []);
      return { success: true, data: mockResponse.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch spending entries';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  async updateSpendingEntry({ commit }, { budgetId, entryId, entryData }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await budgetsApi.updateSpendingEntry(budgetId, entryId, entryData);
      commit('UPDATE_SPENDING_ENTRY', response.data.entry);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update spending entry';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteSpendingEntry({ commit }, { budgetId, entryId }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      await budgetsApi.deleteSpendingEntry(budgetId, entryId);
      commit('REMOVE_SPENDING_ENTRY', entryId);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete spending entry';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchSpendingCategories({ commit }) {
    try {
      const response = await budgetsApi.getSpendingCategories();
      commit('SET_SPENDING_CATEGORIES', response.data.categories || []);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch spending categories';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },

  clearCurrentBudget({ commit }) {
    commit('SET_CURRENT_BUDGET', null);
  },
};

const calculateBudgetTotal = (budget) => {
  if (!budget || !budget.categories) return 0;
  return Object.values(budget.categories)
    .reduce((sum, category) => sum + (category.amount || 0), 0);
};

const calculateSpentTotal = (budget) => {
  if (!budget) return 0;

  // Calculate from performance data if available
  if (budget.performance && budget.performance.overall) {
    return budget.performance.overall.totalActual || 0;
  }

  // Try recent_tracking from new API format
  if (budget.recent_tracking) {
    return budget.recent_tracking.reduce(
      (total, tracking) => total + (tracking.amount || 0), 0,
    );
  }

  // Fallback to budget_trackings
  if (budget.budget_trackings) {
    return budget.budget_trackings.reduce(
      (total, tracking) => total + (tracking.actual_amount || 0), 0,
    );
  }

  return 0;
};

const getters = {
  activeBudgets: (currentState) => (currentState.budgets || []).filter((budget) => budget.status === 'active'),
  currentBudget: (currentState) => currentState.currentBudget,
  budgetTemplates: (currentState) => currentState.budgetTemplates,
  spendingCategories: (currentState) => currentState.spendingCategories,
  spendingEntries: (currentState) => currentState.spendingEntries,

  // Global totals across ALL budgets
  totalSystemBudgetAmount: (currentState) => currentState.budgets
    .reduce((sum, budget) => sum + calculateBudgetTotal(budget), 0),

  totalSystemSpentAmount: (currentState) => currentState.budgets
    .reduce((sum, budget) => sum + calculateSpentTotal(budget), 0),

  totalBudgetAmount: (currentState) => calculateBudgetTotal(currentState.currentBudget),
  totalSpentAmount: (currentState) => calculateSpentTotal(currentState.currentBudget),

  budgetProgress: (currentState, currentGetters) => {
    const total = currentGetters.totalBudgetAmount;
    if (total === 0) return 0;
    return Math.round((currentGetters.totalSpentAmount / total) * 100);
  },

  categoriesOverBudget: (currentState) => {
    const budget = currentState.currentBudget;
    if (!budget) return [];

    const categories = [];
    Object.entries(budget.categories || {}).forEach(([name, category]) => {
      const allocatedAmount = category.amount || 0;
      let spentAmount = 0;

      // Get spent amount using the same logic as calculateSpentTotal but per category
      if (budget.performance?.categories?.[name]) {
        spentAmount = budget.performance.categories[name].actual || 0;
      } else if (budget.recent_tracking) {
        spentAmount = budget.recent_tracking
          .filter((tracking) => tracking.category === name)
          .reduce((total, tracking) => total + (tracking.amount || 0), 0);
      } else if (budget.budget_trackings) {
        spentAmount = budget.budget_trackings
          .filter((tracking) => tracking.category === name)
          .reduce((total, tracking) => total + (tracking.actual_amount || 0), 0);
      }

      if (spentAmount > allocatedAmount) {
        categories.push({ name, amount: allocatedAmount, spent_amount: spentAmount });
      }
    });

    return categories;
  },
  getBudgetById: (currentState) => (id) => currentState.budgets.find(
    (budget) => budget.id === parseInt(id, 10),
  ) || null,
  getBudgetExpenses: (currentState) => (budgetId) => currentState.spendingEntries.filter(
    (entry) => entry.budget_id === parseInt(budgetId, 10),
  ),
  isLoading: (currentState) => currentState.loading,
  error: (currentState) => currentState.error,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
